import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import * as pdfjsLib from 'pdfjs-dist';
import { AnnotationContext, PdfOutlinePanel } from './PdfOutlinePanel';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import './SimplePDFViewer.css';

// Configure PDF.js worker globally - served locally from public/ (copied by
// scripts/setup-pdfjs-assets.js postinstall). No external CDN dependency.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Note: PDF.js font warnings ("TT: undefined function: 32") are harmless and come from the worker thread.
// They indicate that some PDF fonts use features not fully supported, but rendering still works correctly.

interface SimplePDFViewerProps {
  file: File;
  onExternalLink?: (linkPath: string, pageNumber?: number, namedDestination?: string) => void;
  initialPage?: number; // Page number to jump to when document loads (1-based)
  initialNamedDestination?: string; // Named destination to jump to when document loads
  annotation?: AnnotationContext; // Enables the shared review-comments tab when present
}

export interface SimplePDFViewerRef {
  goBack: () => void;
  goForward: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  currentPage: number;
  jumpToPage: (pageNumber: number) => void;
}

export const SimplePDFViewer = forwardRef<SimplePDFViewerRef, SimplePDFViewerProps>(({ file, onExternalLink, initialPage, initialNamedDestination, annotation }, ref) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  // Page/history tracking lives in refs, NOT state. This component must not
  // re-render on page changes: the react-pdf-viewer plugin factories below run
  // on every render and produce new sidebar component types, so each re-render
  // would remount the sidebar - collapsing bookmark expansion and flickering.
  // The parent reads these values through the imperative handle's getters.
  const canGoBackRef = useRef(false);
  const canGoForwardRef = useRef(false);
  const currentPageRef = useRef(1);
  const pageHistoryRef = useRef<number[]>([1]);
  const historyIndexRef = useRef(0);
  const isNavigatingProgrammaticallyRef = useRef(false);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const zoomSetRef = useRef(false); // Track if zoom has been set to prevent multiple attempts
  const pdfDocRef = useRef<any>(null); // Store reference to PDF document
  // Bridge to the bookmark panel: it registers a setter here, and onDocumentLoad
  // pushes the loaded doc through it. Keeps doc delivery out of React state so
  // this component does not re-render (see plugin-factory notes below).
  const outlineDocSetterRef = useRef<((doc: any | null) => void) | null>(null);
  const jumpToPageRef = useRef<((pageIndex: number) => void) | null>(null);

  // Worker URL for PDF.js - local copy, no CDN dependency
  const workerUrl = '/pdf.worker.min.js';

  // Create URL from file
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    // Reset page history when file changes
    pageHistoryRef.current = [1];
    historyIndexRef.current = 0;
    currentPageRef.current = 1;
    canGoBackRef.current = false;
    canGoForwardRef.current = false;
    zoomSetRef.current = false; // Reset zoom flag when file changes

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Handle external link clicks - intercept before navigation
  useEffect(() => {
    if (!onExternalLink || !fileUrl) return;

    const handleLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Try multiple selectors for PDF links
      let linkElement: HTMLElement | null = null;
      let href: string | null = null;
      
      // Check if target itself is a link
      if (target.tagName === 'A') {
        linkElement = target;
        href = target.getAttribute('href');
      } else {
        // Try to find link in parent chain
        linkElement = target.closest('a[href]') || 
                     target.closest('a[data-target]') ||
                     target.closest('.rpv-core__annotation--link a') ||
                     target.closest('.rpv-core__annotation--link');
        
        if (linkElement) {
          href = linkElement.getAttribute('href') || 
                linkElement.getAttribute('data-target') ||
                (linkElement as any).dataset?.target ||
                (linkElement as any).getAttribute?.('data-target');
        }
      }
      
      // Also check if the click is on an element with a data-target attribute
      if (!href && target.hasAttribute('data-target')) {
        href = target.getAttribute('data-target');
        linkElement = target;
      }
      
      if (href && linkElement) {
        // Never intercept browser-internal URLs or download links (e.g. the
        // temporary <a download href="blob:..."> anchors created by file
        // exports). These are not eCTD document links.
        if (href.startsWith('blob:') || href.startsWith('data:') || linkElement.hasAttribute('download')) {
          return;
        }

        // Check if it's an internal link (same document) - let PDF.js handle these naturally
        // Internal links include: #page=N, #nameddest=..., or just #
        const isInternalLink = (href.startsWith('#') && !href.startsWith('http')) ||
                              /^\d+$/.test(href) ||
                              href.startsWith('mailto:');
        
        // For internal links, let PDF.js handle navigation naturally - don't intercept
        if (isInternalLink) {
          console.log('🔗 Internal link detected, letting PDF.js handle:', href);
          // Don't prevent default - let PDF.js handle internal navigation
          return;
        }
        
        // Check if it's a localhost URL (same origin) - extract path
        const isLocalhostUrl = href.startsWith('http://localhost') || 
                               href.startsWith('https://localhost') ||
                               href.startsWith('http://127.0.0.1');
        
        // Check if it's a PDF file link (ends with .pdf or has .pdf in path)
        const isPdfLink = /\.pdf/i.test(href);
        
        // Intercept if: localhost URL, or PDF link, or relative path with slashes
        if (isLocalhostUrl || (!isInternalLink && (isPdfLink || href.includes('/')))) {
          console.log('🔗 Intercepted link click:', href);
          console.log('🔗 Is localhost URL:', isLocalhostUrl);
          console.log('🔗 Is internal link:', isInternalLink);
          console.log('🔗 Is PDF link:', isPdfLink);
          
          // Prevent default navigation immediately - CRITICAL
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          let linkPath = '';
          let pageNumber: number | undefined;
          let namedDestination: string | undefined;
          
          // FIRST: Extract page number from URL hash SYNCHRONOUSLY (before async processing)
          // This ensures we have the page number immediately for external links
          // Try multiple regex patterns to extract page number
          let destArrayMatch = href.match(/#\[(\d+),/);
          if (!destArrayMatch) {
            // Try without the # prefix
            destArrayMatch = href.match(/\[(\d+),/);
          }
          
          if (destArrayMatch) {
            // PDF.js uses 0-based page indexing in destination arrays
            // So [36,...] means page 37 (0-based index 36)
            const zeroBasedIndex = parseInt(destArrayMatch[1], 10);
            pageNumber = zeroBasedIndex + 1; // Convert to 1-based
          } else {
            // Check for standard #page=5 format
            const hashMatch = href.match(/#page=(\d+)/i);
            if (hashMatch) {
              pageNumber = parseInt(hashMatch[1], 10);
            } else {
              // Check for named destination
              const namedDestMatch = href.match(/#nameddest=([^&]+)/i) || href.match(/#([^?&]+)/);
              if (namedDestMatch && !namedDestMatch[1].startsWith('http') && !namedDestMatch[1].startsWith('[')) {
                namedDestination = decodeURIComponent(namedDestMatch[1]);
              }
            }
          }
          
          // Extract path from href (synchronously, before async processing)
          if (isLocalhostUrl) {
            try {
              const url = new URL(href);
              linkPath = url.pathname.replace(/^\/+/, '');
              console.log('🔗 Extracted path from URL:', linkPath);
            } catch (e) {
              console.warn('🔗 URL parsing failed, trying regex:', e);
              const match = href.match(/https?:\/\/[^\/]+(\/.+?)(?:#|\?|$)/);
              if (match && match[1]) {
                linkPath = match[1].replace(/^\/+/, '');
                console.log('🔗 Extracted path via regex:', linkPath);
              }
            }
          } else {
            // Extract path from relative path or filename
            linkPath = href.replace(/\\/g, '/');
            // Split by # first to preserve hash for page/destination extraction
            const [pathPart, hashPart] = linkPath.split('#');
            linkPath = pathPart.split('?')[0];
            linkPath = linkPath.replace(/^\/+/, '');
            
            // If page number wasn't extracted from href yet, try extracting from hashPart
            if (!pageNumber && !namedDestination && hashPart) {
              const destArrayMatch = hashPart.match(/^\[(\d+),/);
              if (destArrayMatch) {
                const zeroBasedIndex = parseInt(destArrayMatch[1], 10);
                pageNumber = zeroBasedIndex + 1; // Convert to 1-based
                console.log('🔗 Extracted page number from hashPart:', pageNumber, '(0-based index:', zeroBasedIndex, ')');
              } else {
                const hashPageMatch = hashPart.match(/page=(\d+)/i);
                if (hashPageMatch) {
                  pageNumber = parseInt(hashPageMatch[1], 10);
                } else {
                  const hashNamedDestMatch = hashPart.match(/nameddest=([^&]+)/i) || hashPart.match(/^([^?&]+)/);
                  if (hashNamedDestMatch && !hashNamedDestMatch[1].startsWith('[')) {
                    namedDestination = decodeURIComponent(hashNamedDestMatch[1]);
                  }
                }
              }
            }
            
            console.log('🔗 Extracted path from relative:', linkPath);
          }
          
          // Call onExternalLink IMMEDIATELY with extracted page number
          if (linkPath) {
            onExternalLink(linkPath, pageNumber, namedDestination);
          } else {
            console.warn('🔗 No link path extracted from:', href);
          }
          
          // Process link asynchronously to resolve PDF annotation destination (for additional info)
          // This runs in background but doesn't block the onExternalLink call
          (async () => {
            // Try to get page number from PDF annotation destination (only if not already extracted)
            // We need to find the annotation by matching the href with annotations in the PDF
            if (pdfDocRef.current && href && !pageNumber) {
              try {
                // Get all pages and search for the annotation that matches this href
                const numPages = pdfDocRef.current.numPages;
                let foundAnnotation: any = null;
                
                // Search through all pages to find the annotation
                for (let pageNum = 1; pageNum <= numPages && !foundAnnotation; pageNum++) {
                  try {
                    const page = await pdfDocRef.current.getPage(pageNum - 1); // 0-based
                    const annotations = await page.getAnnotations();
                    
                    // Look for link annotations that match this href
                    for (const ann of annotations) {
                      if (ann.subtype === 'Link') {
                        // Check if this annotation's URL matches our href
                        const annUrl = ann.url || ann.uri || '';
                        const normalizedHref = href.replace(/^\.\//, '').replace(/\\/g, '/');
                        const normalizedAnnUrl = annUrl.replace(/^\.\//, '').replace(/\\/g, '/');
                        
                        // Match if URLs are similar (accounting for relative paths)
                        if (annUrl === href || 
                            normalizedAnnUrl === normalizedHref ||
                            annUrl.endsWith(href) ||
                            href.endsWith(annUrl) ||
                            (annUrl && href && (annUrl.includes(href.split('/').pop()!) || href.includes(annUrl.split('/').pop()!)))) {
                          foundAnnotation = ann;
                          console.log('🔗 Found matching annotation on page', pageNum, ':', ann);
                          break;
                        }
                      }
                    }
                  } catch (err) {
                    console.warn(`🔗 Error checking page ${pageNum}:`, err);
                  }
                }
                
                // If we found an annotation, resolve its destination
                if (foundAnnotation && foundAnnotation.dest) {
                  console.log('🔗 Found annotation with destination:', foundAnnotation.dest);
                  
                  try {
                    if (typeof foundAnnotation.dest === 'string') {
                      // Named destination - resolve it
                      const resolvedDest = await pdfDocRef.current.getDestination(foundAnnotation.dest);
                      if (resolvedDest && Array.isArray(resolvedDest) && resolvedDest.length > 0) {
                        const pageIndex = await pdfDocRef.current.getPageIndex(resolvedDest[0]);
                        pageNumber = pageIndex + 1; // Convert to 1-based
                        console.log('🔗 Resolved named destination to page:', pageNumber);
                      }
                    } else if (Array.isArray(foundAnnotation.dest) && foundAnnotation.dest.length > 0) {
                      // Direct page reference
                      const pageIndex = await pdfDocRef.current.getPageIndex(foundAnnotation.dest[0]);
                      pageNumber = pageIndex + 1; // Convert to 1-based
                      console.log('🔗 Resolved destination array to page:', pageNumber);
                    }
                  } catch (err) {
                    console.warn('🔗 Failed to resolve destination:', err);
                  }
                } else if (foundAnnotation && foundAnnotation.dest === undefined && foundAnnotation.url) {
                  // External link - check if URL has page number in hash
                  const urlPageMatch = foundAnnotation.url.match(/#page=(\d+)/i);
                  if (urlPageMatch) {
                    pageNumber = parseInt(urlPageMatch[1], 10);
                    console.log('🔗 Extracted page number from annotation URL:', pageNumber);
                  }
                }
              } catch (err) {
                console.warn('🔗 Error searching PDF annotations:', err);
              }
            }
            
            // Page number and path should already be extracted synchronously above
            // This async section only runs for additional annotation searching (background processing)
            // onExternalLink has already been called synchronously above
          })();
          
          return false;
        }
      }
    };

    // Intercept at multiple levels with capture phase
    const interceptLink = (e: MouseEvent) => {
      handleLinkClick(e);
    };

    // Add to document body with capture to catch all link clicks early
    document.addEventListener('click', interceptLink, true);
    
    // Also add to viewer container when it's ready
    const timeoutId = setTimeout(() => {
      const viewerContainer = viewerContainerRef.current || 
                             document.querySelector('.pdfjs-viewer-host') || 
                             document.querySelector('.rpv-core__viewer') ||
                             document.querySelector('.rpv-core__inner-pages');
      if (viewerContainer) {
        viewerContainer.addEventListener('click', interceptLink, true);
      }
      
      // Intercept at annotation layer level
      const annotationLayers = document.querySelectorAll('.rpv-core__annotation-layer');
      annotationLayers.forEach(layer => {
        layer.addEventListener('click', interceptLink as EventListener, true);
      });
      
      // Also intercept anchor tag clicks directly
      const links = document.querySelectorAll('.rpv-core__annotation-layer a, .rpv-core__viewer a');
      links.forEach(link => {
        link.addEventListener('click', interceptLink as EventListener, true);
      });
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', interceptLink, true);
      
      const viewerContainer = viewerContainerRef.current || 
                             document.querySelector('.pdfjs-viewer-host') || 
                             document.querySelector('.rpv-core__viewer') ||
                             document.querySelector('.rpv-core__inner-pages');
      if (viewerContainer) {
        viewerContainer.removeEventListener('click', interceptLink, true);
      }
      
      const annotationLayers = document.querySelectorAll('.rpv-core__annotation-layer');
      annotationLayers.forEach(layer => {
        layer.removeEventListener('click', interceptLink as EventListener, true);
      });
    };
  }, [onExternalLink, fileUrl]);

  // The plugin factories are hooks (they call useMemo/useState internally), so
  // they must be called unconditionally at the top level on every render - never
  // inside useMemo. Their internal stores are memoized, so plugin state survives
  // re-renders; avoiding sidebar remounts is handled by keeping this component's
  // own tracking in refs (see above) so it simply doesn't re-render mid-document.
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;
  jumpToPageRef.current = jumpToPage;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    // No library sidebar at all: bookmarks AND thumbnails are handled by our
    // own PdfOutlinePanel (single sidebar). With zero tabs the default-layout
    // sidebar renders nothing; its toolbar (zoom, search, etc.) is kept.
    sidebarTabs: () => [],
  });

  useImperativeHandle(
    ref,
    () => ({
      goBack: () => {
        // Navigate back in page history
        if (historyIndexRef.current > 0) {
          isNavigatingProgrammaticallyRef.current = true;
          historyIndexRef.current--;
          const prevPage = pageHistoryRef.current[historyIndexRef.current];
          canGoBackRef.current = historyIndexRef.current > 0;
          canGoForwardRef.current = true;
          // Jump to previous page using react-pdf-viewer's API
          if (jumpToPage) {
            jumpToPage(prevPage - 1); // react-pdf-viewer uses 0-based indexing
          }
        }
      },
      goForward: () => {
        // Navigate forward in page history
        if (historyIndexRef.current < pageHistoryRef.current.length - 1) {
          isNavigatingProgrammaticallyRef.current = true;
          historyIndexRef.current++;
          const nextPage = pageHistoryRef.current[historyIndexRef.current];
          canGoForwardRef.current = historyIndexRef.current < pageHistoryRef.current.length - 1;
          canGoBackRef.current = true;
          // Jump to next page using react-pdf-viewer's API
          if (jumpToPage) {
            jumpToPage(nextPage - 1); // react-pdf-viewer uses 0-based indexing
          }
        }
      },
      get canGoBack() {
        return canGoBackRef.current;
      },
      get canGoForward() {
        return canGoForwardRef.current;
      },
      get currentPage() {
        return currentPageRef.current;
      },
      jumpToPage: (pageNumber: number) => {
        // pageNumber is 1-based from external calls
        const targetPage = typeof pageNumber === 'number' && pageNumber > 0 ? pageNumber : 1;
        isNavigatingProgrammaticallyRef.current = true;
        if (jumpToPage) {
          jumpToPage(targetPage - 1); // react-pdf-viewer uses 0-based indexing
        }
      },
    }),
    [jumpToPage]
  );

  if (!fileUrl) {
    return (
      <div className="pdfjs-root">
        <div className="pdfjs-status">Loading PDF…</div>
      </div>
    );
  }

  return (
    <div className="pdfjs-root">
      <div className="pdfjs-content">
        <PdfOutlinePanel
          registerDocSetter={(setter) => {
            outlineDocSetterRef.current = setter;
            if (pdfDocRef.current) setter(pdfDocRef.current);
          }}
          onNavigate={(pageIndex) => {
            // Bookmark navigation counts as user navigation: let onPageChange
            // record it in the page history so Back returns to where you were.
            jumpToPageRef.current?.(pageIndex);
          }}
          annotation={annotation}
          getCurrentPage={() => currentPageRef.current}
        />
        <Worker workerUrl={workerUrl}>
          <div className="pdfjs-viewer-host" ref={viewerContainerRef} style={{ height: '100%' }}>
            <Viewer
              fileUrl={fileUrl}
              plugins={[pageNavigationPluginInstance, defaultLayoutPluginInstance]}
              defaultScale={SpecialZoomLevel.PageFit}
              onDocumentLoad={(e) => {
                // Store PDF document reference for resolving destinations
                pdfDocRef.current = e.doc;
                // Feed the bookmark panel (no state involved - no re-render)
                outlineDocSetterRef.current?.(e.doc);
                
                // Reset history when document loads
                let startPage = initialPage && initialPage > 0 ? initialPage : 1;
                pageHistoryRef.current = [startPage];
                historyIndexRef.current = 0;
                currentPageRef.current = startPage;
                canGoBackRef.current = false;
                canGoForwardRef.current = false;
                
                // Resolve named destination to page number if provided
                if (initialNamedDestination && e.doc) {
                  e.doc.getDestination(initialNamedDestination).then((dest: any) => {
                    if (dest && Array.isArray(dest) && dest.length > 0) {
                      // dest[0] is the page reference, dest[1] is the view type
                      e.doc.getPageIndex(dest[0]).then((pageIndex: number) => {
                        const resolvedPage = pageIndex + 1; // Convert to 1-based
                        startPage = resolvedPage;
                        pageHistoryRef.current = [resolvedPage];
                        currentPageRef.current = resolvedPage;
                        if (jumpToPage) {
                          isNavigatingProgrammaticallyRef.current = true;
                          jumpToPage(pageIndex); // react-pdf-viewer uses 0-based indexing
                        }
                      }).catch((err: any) => {
                        console.warn('🔗 Failed to resolve named destination to page:', err);
                        // Fall back to initial page if provided
                        if (initialPage && initialPage > 0 && jumpToPage) {
                          setTimeout(() => {
                            isNavigatingProgrammaticallyRef.current = true;
                            jumpToPage(initialPage - 1);
                          }, 100);
                        }
                      });
                    } else {
                      console.warn('🔗 Named destination returned invalid result:', dest);
                      // Fall back to initial page if provided
                      if (initialPage && initialPage > 0 && jumpToPage) {
                        setTimeout(() => {
                          isNavigatingProgrammaticallyRef.current = true;
                          jumpToPage(initialPage - 1);
                        }, 100);
                      }
                    }
                  }).catch((err: any) => {
                    console.warn('🔗 Failed to get destination for:', initialNamedDestination, err);
                    // Fall back to initial page if provided
                    if (initialPage && initialPage > 0 && jumpToPage) {
                      setTimeout(() => {
                        isNavigatingProgrammaticallyRef.current = true;
                        jumpToPage(initialPage - 1);
                      }, 100);
                    }
                  });
                } else if (initialPage && initialPage > 0) {
                  // Jump to initial page if specified (no named destination)
                  // Use multiple attempts with increasing delays to ensure the viewer is ready
                  const attemptJump = (attempt: number = 1) => {
                    if (jumpToPage) {
                      try {
                        isNavigatingProgrammaticallyRef.current = true;
                        const targetPage = initialPage - 1; // react-pdf-viewer uses 0-based indexing
                        jumpToPage(targetPage);
                        // Verify after a short delay
                        setTimeout(() => {
                          if (currentPageRef.current !== initialPage && attempt < 3) {
                            attemptJump(attempt + 1);
                          }
                        }, 200);
                      } catch (err) {
                        if (attempt < 3) {
                          setTimeout(() => attemptJump(attempt + 1), 200 * attempt);
                        }
                      }
                    } else if (attempt < 5) {
                      // jumpToPage not ready yet, try again
                      setTimeout(() => attemptJump(attempt + 1), 100 * attempt);
                    }
                  };
                  // Start first attempt after a short delay
                  setTimeout(() => attemptJump(), 150);
                }
              }}
              onPageChange={(e) => {
                // Track page changes for navigation history
                const newPage = e.currentPage + 1; // react-pdf-viewer uses 0-based indexing

                // If we're navigating programmatically, just update current page without modifying history
                if (isNavigatingProgrammaticallyRef.current) {
                  isNavigatingProgrammaticallyRef.current = false;
                  currentPageRef.current = newPage;
                  return;
                }

                // Only add to history if it's a user-initiated navigation (like clicking a link)
                if (newPage !== currentPageRef.current) {
                  // If we're not at the end of history, truncate forward history
                  if (historyIndexRef.current < pageHistoryRef.current.length - 1) {
                    pageHistoryRef.current = pageHistoryRef.current.slice(0, historyIndexRef.current + 1);
                  }
                  // Add new page to history
                  pageHistoryRef.current.push(newPage);
                  historyIndexRef.current = pageHistoryRef.current.length - 1;
                  currentPageRef.current = newPage;
                  canGoBackRef.current = historyIndexRef.current > 0;
                  canGoForwardRef.current = false;
                }
              }}
            />
          </div>
        </Worker>
      </div>
    </div>
  );
});
